import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { authApi } from "../api/auth";
import type { ApiError } from "../api/client";
import { authFallbackError, looksLikeEmail } from "../lib/authErrors";

export default function ForgotPasswordPage() {
  const { t } = useTranslation("auth");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const address = email.trim();
    if (!looksLikeEmail(address)) {
      setError(t("errors.emailInvalid", "Unesite celu adresu e-pošte, npr. ime@firma.rs."));
      return;
    }
    setSubmitting(true);
    try {
      // 204 whether or not the account exists, so an error here is a real failure, not a "no".
      await authApi.forgotPassword(address);
      setDone(true);
    } catch (err) {
      setError(
        (err as ApiError).code === "VALIDATION"
          ? t("errors.emailInvalid", "Unesite celu adresu e-pošte, npr. ime@firma.rs.")
          : authFallbackError(err, t),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={shell}>
      <div style={card}>
        <h1 style={{ margin: 0, color: "var(--brand-primary)", fontSize: 24 }}>{t("forgot.title")}</h1>

        {done ? (
          <>
            <p style={{ color: "var(--ink-3)", fontSize: 14, lineHeight: 1.6 }}>
              {t("forgot.sent")}
            </p>
            <Link to="/login" style={{ color: "var(--brand-primary)", fontSize: 14 }}>
              {t("forgot.back")}
            </Link>
          </>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }} noValidate>
            <p style={{ color: "var(--ink-3)", fontSize: 14, margin: 0 }}>
              {t("forgot.intro")}
            </p>
            {error && <div className="auth-error" role="alert">{error}</div>}
            <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 14 }}>
              <span>{t("login.email")}</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                style={input}
              />
            </label>
            <button type="submit" disabled={submitting} style={btn}>
              {submitting ? t("forgot.submitting") : t("forgot.submit")}
            </button>
            <Link to="/login" style={{ color: "var(--brand-primary)", fontSize: 14, textAlign: "center" }}>
              {t("forgot.back")}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}

const shell: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--bg)",
  padding: 16,
};
const card: React.CSSProperties = {
  width: "100%",
  maxWidth: 400,
  background: "var(--surface)",
  padding: 32,
  borderRadius: 12,
  boxShadow: "0 1px 3px rgba(0,0,0,.08)",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};
const input: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 6,
  border: "1px solid var(--border-strong)",
  // Explicit so the dark theme does not keep the browser's white field with light text.
  background: "var(--surface)",
  color: "var(--ink)",
  fontSize: 15,
};
const btn: React.CSSProperties = {
  padding: "12px 16px",
  background: "var(--brand-primary)",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
};
